import { Objective, ObjectiveStatus } from "@/types";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { OkrStatusBadge } from "./OkrStatusBadge";
import { UserAvatar } from "@/components/UserAvatar";
import { MoreHorizontal, Pencil, Plus, Trash2, ChevronRight, GripVertical, ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { DraggableProvidedDragHandleProps } from "@hello-pangea/dnd";
import { TREE_ROW_BASE, TREE_ROW_COLS } from "./okr-tree-constants";
import { CATEGORY_BADGE, CATEGORY_LABEL, okrPct } from "./okrConstants";
import { dueStatusOf } from "./okr-dates";
import { DueBadge } from "./DueBadge";

interface ObjectiveCardProps {
    objective: Objective;
    onView?: (objective: Objective) => void;
    onEdit?: (objective: Objective) => void;
    onDelete?: (objective: Objective) => void;
    onAddKeyResult?: (objective: Objective) => void;
    expanded?: boolean;
    onToggleExpand?: () => void;
    dragHandleProps?: DraggableProvidedDragHandleProps | null;
    /** Objective writes are C-level only; hides the action menu otherwise */
    canManage?: boolean;
}

export function ObjectiveCard({
    objective, onView, onEdit, onDelete,
    onAddKeyResult, expanded, onToggleExpand, dragHandleProps, canManage = false,
}: ObjectiveCardProps) {
    const pct = okrPct(objective.progress, objective.targetProgress);
    const dueStatus = dueStatusOf(objective.dueDate, objective.status === ObjectiveStatus.ACTIVE);

    return (
        <div
            className={cn(TREE_ROW_BASE, "cursor-pointer hover:bg-muted/30")}
            style={TREE_ROW_COLS}
            onClick={() => onView?.(objective)}
        >
            {/* Left accent bar */}
            <div className={cn(
                "absolute left-0 inset-y-1.5 w-[3px] rounded-r bg-violet-500 transition-opacity",
                expanded ? "opacity-100" : "opacity-0 group-hover:opacity-100",
            )} />

            {/* Name cell */}
            <div className="flex items-center min-w-0 gap-0">
                {dragHandleProps && (
                    <button type="button" {...dragHandleProps} onClick={e => e.stopPropagation()}
                        className="text-muted-foreground/40 hover:text-muted-foreground cursor-grab mr-1 flex-shrink-0">
                        <GripVertical className="h-3.5 w-3.5" />
                    </button>
                )}
                <button type="button"
                    aria-label={expanded ? "Collapse objective" : "Expand objective"}
                    className="h-[22px] w-[22px] flex-shrink-0 flex items-center justify-center rounded text-muted-foreground hover:bg-accent transition-colors mr-1"
                    onClick={e => { e.stopPropagation(); onToggleExpand?.(); }}>
                    <ChevronRight className={cn("h-3.5 w-3.5 transition-transform duration-200", expanded && "rotate-90")} />
                </button>
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide whitespace-nowrap mr-2 flex-shrink-0 bg-violet-100 text-violet-700 border border-violet-200 dark:bg-violet-950 dark:text-violet-300 dark:border-violet-800">
                    OBJ
                </span>
                <span className="font-mono text-[10px] text-muted-foreground whitespace-nowrap mr-2 flex-shrink-0 min-w-[40px]">
                    {objective.code}
                </span>
                <span className={cn(
                    "inline-flex items-center px-1.5 py-0.5 rounded border text-[9px] font-semibold uppercase tracking-wide whitespace-nowrap mr-2 flex-shrink-0",
                    CATEGORY_BADGE[objective.category],
                )}>
                    {CATEGORY_LABEL[objective.category]}
                </span>
                <div className="flex flex-col min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 min-w-0">
                        <span className="text-[13px] font-semibold truncate">{objective.name}</span>
                        <DueBadge status={dueStatus} />
                    </div>
                    {objective.description && (
                        <span className="text-[11px] text-muted-foreground truncate">{objective.description}</span>
                    )}
                </div>
            </div>

            {/* Status */}
            <div><OkrStatusBadge status={objective.status} size="sm" /></div>

            {/* Progress */}
            <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                <div className="flex-1 h-1.5 bg-border rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-[width] duration-300"
                        style={{ width: `${pct}%`, background: "linear-gradient(90deg, #7C3AED, #6366F1)" }} />
                </div>
                <span className="text-[11px] font-semibold text-muted-foreground w-7 text-right flex-shrink-0">
                    {pct}%
                </span>
            </div>

            {/* Owner */}
            <div className="flex items-center gap-1.5">
                {objective.owner ? (
                    <>
                        <UserAvatar src={objective.owner.avatarUrl} name={objective.owner.fullName} className="h-[22px] w-[22px] flex-shrink-0" />
                        <span className="text-[11px] text-muted-foreground truncate">{objective.owner.fullName}</span>
                    </>
                ) : <span className="text-[11px] text-muted-foreground">—</span>}
            </div>

            {/* Actions */}
            <div className="flex justify-end" onClick={e => e.stopPropagation()}>
                {canManage && <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-6 w-6 flex-shrink-0">
                            <MoreHorizontal className="h-3.5 w-3.5" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        {onView && (
                            <DropdownMenuItem onClick={e => { e.stopPropagation(); onView(objective); }}>
                                <ArrowUpRight className="mr-2 h-4 w-4" /> View
                            </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={e => { e.stopPropagation(); onEdit?.(objective); }}>
                            <Pencil className="mr-2 h-4 w-4" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={e => { e.stopPropagation(); onAddKeyResult?.(objective); }}>
                            <Plus className="mr-2 h-4 w-4" /> Add Key Result
                        </DropdownMenuItem>
                        <DropdownMenuItem className="text-destructive"
                            onClick={e => { e.stopPropagation(); onDelete?.(objective); }}>
                            <Trash2 className="mr-2 h-4 w-4" /> Delete
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>}
            </div>
        </div>
    );
}
